import { MigrationStep, MIGRATION_STEPS } from '@ytl/common-model';
import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { migrationResource, MigrationStateResponse } from '../../../shared/resource/migration.resource';

export enum MigrationPhase {
  Loading = 'loading',
  Intro = 'intro',
  Verification = 'verification',
  Success = 'success',
  Lockout = 'lockout',
  NotRelevant = 'notRelevant',
  Error = 'error',
  NotFound = 'notFound',
}

interface MigrationContextType {
  phase: MigrationPhase;
  setPhase: (phase: MigrationPhase) => void;
  isLoading: boolean;
  migrationData: MigrationStateResponse | null;
  migraSessionId: string | null;
  returnUrl: string | null;
  isGroup2: boolean;
  formattedRecommendedPhone: string;
  handleStartMigration: () => void;
  handleReturnToApp: () => void;
  updateStep: (stepId: MigrationStep) => Promise<void>;
}

const MigrationContext = createContext<MigrationContextType | null>(null);

export const MigrationProvider = ({ children }: { children: ReactNode }) => {
  const [searchParams] = useSearchParams();

  const migraSessionId = searchParams.get('migraSessionId');
  const returnUrl = searchParams.get('returnUrl');

  const [phase, setPhase] = useState<MigrationPhase>(MigrationPhase.Loading);
  const [migrationData, setMigrationData] = useState<MigrationStateResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Recommended phone number from DB
  const recommendedPhoneNumber = migrationData?.data?.RecommendedPhoneNumber;

  // Convert phone number to string for VerificationComponent
  // NOTE: Do NOT add formatting (spaces, + sign) here - let VerificationComponent handle it
  const formatPhoneNumber = useCallback((phoneNumber: number | undefined): string => {
    if (!phoneNumber) return '';
    return phoneNumber.toString();
  }, []);

  const formattedRecommendedPhone = formatPhoneNumber(recommendedPhoneNumber);

  // Initial load - check migration state
  useEffect(() => {
    const checkMigrationState = async () => {
      if (!migraSessionId) {
        console.error('No migraSessionId in URL');
        setPhase(MigrationPhase.Error);
        return;
      }

      if (!returnUrl) {
        console.error('No returnUrl in URL');
        setPhase(MigrationPhase.Error);
        return;
      }

      try {
        setIsLoading(true);
        const response = await migrationResource.getMigrationState(migraSessionId);
        console.log('[MigrationContext] API response:', response);
        setMigrationData(response);

        if (!response.found) {
          setPhase(MigrationPhase.NotFound);
          return;
        }

        // DONE -> redirect back (migration already completed)
        if (response.state === 'DONE') {
          window.location.href = decodeURIComponent(returnUrl);
          return;
        }

        // AMBIGUOUS, NOTRELEVANT -> show error screen
        if (response.state === 'AMBIGUOUS' || response.state === 'NOTRELEVANT') {
          setPhase(MigrationPhase.NotRelevant);
          return;
        }

        // Category based decision - case insensitive
        const category = response.category?.toUpperCase();
        switch (category) {
          case 'GROUP1':
            setPhase(MigrationPhase.Intro);
            // Intro képernyő megjelent - lépés rögzítése
            try {
              console.log('[Migration] Intro screen shown for Group1');
              await migrationResource.updateLastStep(migraSessionId, MIGRATION_STEPS.G1_INTRO_SHOWN);
            } catch (stepErr) {
              console.error('[Migration] Failed to update intro step:', stepErr);
            }
            break;
          case 'GROUP2':
            setPhase(MigrationPhase.Intro);
            // Intro képernyő megjelent - lépés rögzítése
            try {
              console.log('[Migration] Intro screen shown for Group2');
              await migrationResource.updateLastStep(migraSessionId, MIGRATION_STEPS.G2_INTRO_SHOWN);
            } catch (stepErr) {
              console.error('[Migration] Failed to update intro step:', stepErr);
            }
            break;
          default:
            // TvOnly, Deletable - should not reach here (NOTRELEVANT)
            console.error('Unexpected category', response.category);
            setPhase(MigrationPhase.Error);
        }
      } catch (err) {
        console.error('Failed to check migration state', err);
        setPhase(MigrationPhase.Error);
      } finally {
        setIsLoading(false);
      }
    };

    checkMigrationState();
  }, [migraSessionId, returnUrl]);

  // Lépés frissítése az adatbázisban
  const updateStep = useCallback(
    async (stepId: MigrationStep) => {
      if (!migraSessionId) return;

      try {
        // SUCCESS step-eket mindig engedjük (Success screen-en vagyunk)
        const isSuccessStep = stepId === MIGRATION_STEPS.G1_SUCCESS || stepId === MIGRATION_STEPS.G2_SUCCESS;

        if (!isSuccessStep) {
          // Nem SUCCESS step - ellenőrizzük hogy State='DONE'-e már
          console.log('[Migration] Checking state before updating step:', stepId);
          const currentState = await migrationResource.getMigrationState(migraSessionId);

          if (currentState.state === 'DONE') {
            console.log('[Migration] Migration already completed (DONE), redirecting to returnUrl');
            if (returnUrl) {
              window.location.href = decodeURIComponent(returnUrl);
            }
            return;
          }
        }

        // Update step
        console.log('[Migration] Updating step:', stepId);
        const result = await migrationResource.updateLastStep(migraSessionId, stepId);
        console.log('[Migration] Step updated successfully:', stepId, result);
      } catch (err) {
        console.error('[Migration] Failed to update step:', stepId, err);
      }
    },
    [migraSessionId, returnUrl],
  );

  // Handle "Tovább" button on Intro screen -> go to VerificationComponent
  const handleStartMigration = useCallback(async () => {
    // G1: telefonszám választó képernyő megjelenik
    // G2: időpontfoglaló képernyő megjelenik (TODO: G2-nek külön lépés kell majd)
    await updateStep(MIGRATION_STEPS.G1_PHONE_SCREEN_SHOWN);
    setPhase(MigrationPhase.Verification);
  }, [updateStep]);

  // Handle return to app
  const handleReturnToApp = useCallback(() => {
    if (returnUrl) {
      window.location.href = decodeURIComponent(returnUrl);
    }
  }, [returnUrl]);

  // Check if it's Group2 (for different UI if needed) - case insensitive
  const isGroup2 = migrationData?.category?.toUpperCase() === 'GROUP2';

  return (
    <MigrationContext.Provider
      value={{
        phase,
        setPhase,
        isLoading,
        migrationData,
        migraSessionId,
        returnUrl,
        isGroup2,
        formattedRecommendedPhone,
        handleStartMigration,
        handleReturnToApp,
        updateStep,
      }}
    >
      {children}
    </MigrationContext.Provider>
  );
};

export const useMigration = () => {
  const context = useContext(MigrationContext);
  if (!context) {
    throw new Error('useMigration must be used within a MigrationProvider');
  }
  return context;
};
